<template>
  <!-- Accountant doc 2026-10-03 item 1.5: what the automatic cancellation
       compensation decided for this booking, or why it did not apply, and
       the manual grant for a cancellation whose approach was not measured,
       and "process now" for an eligible one whose queued job was lost. -->
  <v-card class="compensation-section" v-if="bookingId">
    <v-card-title class="compensation-header">
      <span>{{ $t("cancellationCompensation.title") }}</span>
      <v-spacer></v-spacer>
      <v-chip v-if="info && info.reason_code" small :color="outcomeColor" text-color="white">
        {{ outcomeLabel }}
      </v-chip>
    </v-card-title>

    <v-card-text>
      <div v-if="loading" class="text-center py-3">
        <v-progress-circular indeterminate color="primary" size="24"></v-progress-circular>
      </div>

      <div v-else-if="loadError" class="red--text py-2">{{ loadError }}</div>

      <template v-else-if="info">
        <p class="grey--text text--darken-1 mb-2">{{ reasonText }}</p>

        <table v-if="row" class="compensation-table">
          <tbody>
            <tr>
              <th>{{ $t("cancellationCompensation.basis") }}</th>
              <td>{{ basisLabel }}</td>
            </tr>
            <tr>
              <th>{{ $t("cancellationCompensation.statusBefore") }}</th>
              <td>{{ row.status_before_cancel || "—" }}</td>
            </tr>
            <tr v-if="row.gps_km !== null && row.gps_km !== undefined">
              <th>{{ $t("cancellationCompensation.gpsKm") }}</th>
              <td>{{ km(row.gps_km) }}</td>
            </tr>
            <tr v-if="row.reference_km !== null && row.reference_km !== undefined">
              <th>{{ $t("cancellationCompensation.referenceKm") }}</th>
              <td>{{ km(row.reference_km) }}</td>
            </tr>
            <tr v-if="row.meta && row.meta.reference_source">
              <th>{{ $t("cancellationCompensation.referenceSource") }}</th>
              <td>{{ referenceSourceLabel }}</td>
            </tr>
            <tr v-if="row.meta && row.meta.arrived_at_pickup !== undefined">
              <th>{{ $t("cancellationCompensation.arrivedAtPickup") }}</th>
              <td>{{ arrivedLabel }}</td>
            </tr>
            <tr v-if="row.cap_km !== null && row.cap_km !== undefined">
              <th>{{ $t("cancellationCompensation.capKm") }}</th>
              <td>{{ km(row.cap_km) }}</td>
            </tr>
            <tr>
              <th>{{ $t("cancellationCompensation.approachKm") }}</th>
              <td>{{ km(row.approach_km) }}</td>
            </tr>
            <tr>
              <th>{{ $t("cancellationCompensation.freeKm") }}</th>
              <td>{{ km(row.free_km) }}</td>
            </tr>
            <tr>
              <th>{{ $t("cancellationCompensation.billableKm") }}</th>
              <td>{{ km(row.billable_km) }}</td>
            </tr>
            <tr>
              <th>{{ $t("cancellationCompensation.rate") }}</th>
              <td>{{ row.rate | money }}</td>
            </tr>
            <tr>
              <th>{{ $t("cancellationCompensation.amount") }}</th>
              <td><strong>{{ row.amount | money }}</strong></td>
            </tr>
            <tr v-if="row.journal_reference_no || row.journal_entry_id">
              <th>{{ $t("cancellationCompensation.journalEntry") }}</th>
              <td>{{ row.journal_reference_no || ("#" + row.journal_entry_id) }}</td>
            </tr>
            <tr v-if="row.granted_by">
              <th>{{ $t("cancellationCompensation.grantedBy") }}</th>
              <td>{{ row.granted_by }}</td>
            </tr>
            <tr v-if="row.granted_at">
              <th>{{ $t("cancellationCompensation.grantedAt") }}</th>
              <td>{{ row.granted_at }}</td>
            </tr>
            <tr v-if="row.meta && row.meta.note">
              <th>{{ $t("cancellationCompensation.note") }}</th>
              <td>{{ row.meta.note }}</td>
            </tr>
            <tr v-if="row.error">
              <th>{{ $t("cancellationCompensation.error") }}</th>
              <td class="red--text">{{ row.error }}</td>
            </tr>
          </tbody>
        </table>

        <!-- Lost queued job (NOT_PROCESSED, no audit row): run the automatic
             decision now. Same permission as the manual grant. -->
        <div v-if="info.can_process && canGrant" class="mt-3">
          <v-btn color="primary" :loading="processing" :disabled="granting" @click="processNow">
            {{ $t("cancellationCompensation.processNow") }}
          </v-btn>
          <p class="caption grey--text mb-0">{{ $t("cancellationCompensation.processNowHint") }}</p>
        </div>

        <!-- Manual grant: a NO_DATA / FAILED row, or a NOT_PROCESSED booking
             with no row, only with the finance-adjustments permission (the
             backend checks both). -->
        <div v-if="info.can_grant_manually && canGrant" class="mt-3">
          <v-layout row wrap align-center>
            <v-flex xs12 sm4 class="pa-1">
              <v-text-field
                v-model="grantKm"
                :label="$t('cancellationCompensation.grantKm')"
                :hint="$t('cancellationCompensation.grantKmHint')"
                persistent-hint
                type="number"
                min="0"
                step="0.1"
              ></v-text-field>
            </v-flex>
            <v-flex xs12 sm5 class="pa-1">
              <v-text-field v-model="grantNote" :label="$t('cancellationCompensation.grantNote')"></v-text-field>
            </v-flex>
            <v-flex xs12 sm3 class="pa-1">
              <v-btn color="primary" block :loading="granting" :disabled="!grantKmValid || processing" @click="grant">
                {{ $t("cancellationCompensation.grant") }}
              </v-btn>
            </v-flex>
          </v-layout>
          <p v-if="grantPreview !== null" class="caption grey--text mb-0">
            {{ $t("cancellationCompensation.grantPreview", { amount: formatMoney(grantPreview) }) }}
          </p>
        </div>
      </template>
    </v-card-text>
  </v-card>
</template>

<script>
import axios from "axios";
import api from "@/constants/api";

const OUTCOME_COLORS = {
  GRANTED: "green",
  BELOW_THRESHOLD: "grey",
  NO_DATA: "orange",
  FAILED: "red",
  DISABLED: "grey",
};

export default {
  name: "CancellationCompensationPanel",
  props: {
    bookingId: { type: Number, required: true },
    // Re-fetch when the booking status changes (e.g. live cancel).
    status: { type: String, default: null },
  },
  data() {
    return {
      loading: false,
      loadError: null,
      info: null,
      grantKm: "",
      grantNote: "",
      granting: false,
      processing: false,
    };
  },
  computed: {
    row() {
      return this.info ? this.info.compensation : null;
    },
    canGrant() {
      return this.hasPermission("MANAGE_FINANCE_ADJUSTMENTS");
    },
    outcomeColor() {
      const code = this.info ? this.info.reason_code : null;
      return OUTCOME_COLORS[code] || "blue-grey";
    },
    outcomeLabel() {
      const code = this.info ? this.info.reason_code : null;
      return code ? this.$t(`cancellationCompensation.codes.${code}`) : "";
    },
    reasonText() {
      const code = this.info ? this.info.reason_code : null;
      return code ? this.$t(`cancellationCompensation.explain.${code}`) : "";
    },
    referenceSourceLabel() {
      const source = this.row && this.row.meta ? this.row.meta.reference_source : null;
      return source ? this.$t(`cancellationCompensation.referenceSources.${source}`) : "—";
    },
    arrivedLabel() {
      const meta = this.row && this.row.meta;
      if (!meta || meta.arrived_at_pickup === undefined) return "—";
      const label = this.$t(meta.arrived_at_pickup ? "cancellationCompensation.yes" : "cancellationCompensation.no");
      return meta.arrived_to_pickup_m !== undefined && meta.arrived_to_pickup_m !== null
        ? `${label} (${this.$t("cancellationCompensation.metersFromPickup", { m: meta.arrived_to_pickup_m })})`
        : label;
    },
    basisLabel() {
      return this.row && this.row.basis ? this.$t(`cancellationCompensation.bases.${this.row.basis}`) : "—";
    },
    grantKmValid() {
      const n = Number(this.grantKm);
      return this.grantKm !== "" && !isNaN(n) && n > 0;
    },
    // Same formula as the backend (rate x whole km above the free distance,
    // or pro rata), shown before the click; the server's figure is the truth.
    grantPreview() {
      if (!this.grantKmValid || !this.info || !this.info.settings) return null;
      const s = this.info.settings;
      let km = Number(this.grantKm);
      if (s.max_km > 0) km = Math.min(km, s.max_km);
      const over = Math.max(0, Math.round((km - s.free_km) * 1000) / 1000);
      const billable = s.rounding === "prorata" ? over : Math.floor(over);
      return Math.floor(billable * s.per_km);
    },
  },
  watch: {
    status() {
      this.fetch();
    },
    bookingId() {
      this.fetch();
    },
  },
  mounted() {
    this.fetch();
  },
  methods: {
    km(value) {
      if (value === null || value === undefined || value === "") return "—";
      const n = Number(value);
      return isNaN(n) ? "—" : `${Math.round(n * 100) / 100} ${this.$t("cancellationCompensation.kmUnit")}`;
    },
    errorMessage(err, fallbackKey) {
      const data = err && err.response && err.response.data;
      return (data && data.message) || this.$t(`cancellationCompensation.${fallbackKey}`);
    },
    fetch() {
      this.loading = true;
      this.loadError = null;
      axios
        .get(api.ACTIONS.BOOKING_CANCELLATION_COMPENSATION(this.bookingId))
        .then((res) => {
          this.info = (res.data && res.data.data) || null;
        })
        .catch((err) => {
          this.info = null;
          this.loadError = this.errorMessage(err, "loadFailed");
        })
        .finally(() => {
          this.loading = false;
        });
    },
    processNow() {
      if (this.processing) return;
      this.processing = true;
      axios
        .post(api.ACTIONS.BOOKING_CANCELLATION_COMPENSATION_PROCESS(this.bookingId))
        .then((res) => {
          this.info = (res.data && res.data.data) || this.info;
          window.getApp.$emit("SHOW_TOAST", this.$t("cancellationCompensation.processed"));
        })
        .catch((err) => {
          window.getApp.$emit("SHOW_TOAST", this.errorMessage(err, "processFailed"), "red");
        })
        .finally(() => {
          this.processing = false;
        });
    },
    grant() {
      if (!this.grantKmValid || this.processing) return;
      this.granting = true;
      axios
        .post(api.ACTIONS.BOOKING_CANCELLATION_COMPENSATION_GRANT(this.bookingId), {
          km: Number(this.grantKm),
          note: this.grantNote || null,
        })
        .then((res) => {
          this.info = (res.data && res.data.data) || this.info;
          this.grantKm = "";
          this.grantNote = "";
          window.getApp.$emit("SHOW_TOAST", this.$t("cancellationCompensation.granted"));
        })
        .catch((err) => {
          window.getApp.$emit("SHOW_TOAST", this.errorMessage(err, "grantFailed"), "red");
        })
        .finally(() => {
          this.granting = false;
        });
    },
  },
};
</script>

<style scoped>
.compensation-section {
  margin-top: 12px;
}
.compensation-header {
  font-weight: 600;
}
.compensation-table {
  width: 100%;
  border-collapse: collapse;
}
.compensation-table th,
.compensation-table td {
  padding: 4px 8px;
  border-bottom: 1px solid #eee;
  text-align: start;
}
.compensation-table th {
  width: 45%;
  font-weight: 500;
  color: #555;
}
</style>
